<template lang="pug">
.videoCallTrainerTask
  template(v-if="isCallCompleted")
    .result Видеозвонок завершен

  template(v-else-if="isAnswersLoading")
    ui-loader

  template(v-else-if="canStartCall")
    ui-button(
      color="primary"
      @click="onStart"
      :is-disabled="studentVks.isCreating.value || isDisabled"
    ) Ответить (видеозвонок)

  template(v-else-if="isCurrentQuestionCall && studentVks.status.value === 'waiting'")
    .text(v-if="studentVks.queuePosition.value") Вы в очереди: {{ studentVks.queuePosition.value }}
    .text(v-if="studentVks.waitingSeconds.value > 0") Ожидание: {{ studentVks.waitingSeconds.value }} сек
    .waiting
      ui-loader
      .text Ожидание экзаменатора...

  template(v-else-if="isCurrentQuestionCall && studentVks.isActive.value")
    .result Видеосессия открыта поверх страницы

  template(v-else-if="isCurrentQuestionCall && studentVks.status.value === 'done'")
    .result Видеозвонок завершен

  template(v-else-if="isCurrentQuestionCall && studentVks.status.value === 'cancelled'")
    .cancelled
      .result Видеосессия отменена. Вы можете повторить видеовызов.
      ui-button(
        color="primary"
        @click="onStart"
        :is-disabled="studentVks.isCreating.value || isDisabled"
      ) Ответить (видеозвонок)

  template(v-else-if="isCurrentQuestionCall && studentVks.status.value === 'expired'")
    .result Время ожидания истекло

  template(v-else-if="isCurrentQuestionCall && studentVks.status.value === 'failed'")
    .result Ошибка соединения
    ui-button(@click="studentVks.clearTerminalStatus()") Попробовать снова

  template(v-else)
    .result Видеовызов уже запущен
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'
import type { TrainerTaskVideoCallControllerType } from '@abstract-trainer/controllers/TrainerTaskController'
import type TrainerController from '@abstract-trainer/controllers/TrainerController'
import { ProvidedStudentVksController } from '@/modules/VKS/controllers/StudentVksController'

interface Props {
  task: ReturnType<TrainerTaskVideoCallControllerType>
  trainerController: ReturnType<typeof TrainerController>
  isDisabled?: boolean
}

const props = defineProps<Props>()

const studentVks = inject(ProvidedStudentVksController)

if (!studentVks) {
  throw new Error('Student VKS controller is not provided')
}

const courseState = computed(() => props.trainerController.courseState?.value)
const courseId = computed(() => courseState.value?.course?.id ?? null)
const flowId = computed(() => courseState.value?.flow?.id ?? null)
const stageId = computed(() => courseState.value?.currentStageId ?? null)
const questionId = computed(() => props.task.data.id)
const isCurrentQuestionCall = computed(() => studentVks.activeQuestionId.value === questionId.value)
const isCallCompleted = computed(() => props.task.hasRemoteAnswer.value)
const isAnswersLoading = computed(() => props.trainerController.answers.isLoading.value)
const canStartCall = computed(
  () =>
    studentVks.status.value === 'idle' ||
    (!isCurrentQuestionCall.value && studentVks.isTerminal.value)
)

const onStart = () => {
  studentVks.clearTerminalStatus()
  studentVks.start({
    courseId: courseId.value,
    flowId: flowId.value,
    stageId: stageId.value,
    questionId: questionId.value
  })
}
</script>

<style scoped lang="sass">
.videoCallTrainerTask
  .cancelled
    display: flex
    flex-direction: column
    align-items: flex-start
    gap: 2*$u

  .waiting
    display: flex
    flex-direction: column
    align-items: center
    gap: 10px
</style>
